/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getCluMigrationService } from 'src/clu/core/services/migration/cluMigrationService';
import { LuisAppMetadata } from 'src/clu/core/services/migration/cluMigrationSwaggers';
import { getMigrationJobOperationHandler } from 'src/clu/core/services/migration/cluMigrationUtils';
import { CluMigrationJobStore } from 'src/clu/core/stores/CluMigrationJobStore';
import { getTokensService } from 'src/core/services/tokensService';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { isProductionEnvironment } from 'src/core/utils/api/environment';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';

export type CluMigrationHandlerGroup = ReturnType<typeof createCluMigrationHandlerGroup>;

export const createCluMigrationHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { uiStore, rootStore, lifetime } = dependencies;

    const migrateLuisApps = async ({
        luisAppsMetadata,
        languageResource,
        context
    }: HandlerParams<{ luisAppsMetadata: LuisAppMetadata[]; languageResource: AzureCsAccountStore }>) => {
        const { cluMigrationStoreHouse } = rootStore;
        const { cluMigrationJobsStoreHouse } = cluMigrationStoreHouse;
        const { csAccountStore: luisResource } = rootStore.azureStore.session;
        cluMigrationJobsStoreHouse.setItems([]);
        cluMigrationJobsStoreHouse.tracker.setState('running');

        const {
            userTokens: { armToken },
            tenantId
        } = rootStore.azureStore.session;

        let jobId: string;
        const migrationService = getCluMigrationService({ context });

        try {
            let languageResourceKey = await getTokensService().getServiceKey({
                tenantId,
                armToken,
                principalId: rootStore.userStore.principalId,
                resourcePath: languageResource.path,
                resourceKind: languageResource.kind
            });

            if (!isProductionEnvironment()) {
                languageResourceKey = {
                    ...languageResourceKey,
                    value: languageResource?.id,
                    keyType: languageResource?.id ? 'resourceKey' : 'none'
                };
            }

            const statusUrl = await migrationService.initiateMigration({
                luisAppsMetadata,
                languageResourceKey,
                luisRegion: luisResource.region,
                languageResourceEndpoint: languageResource.endpoint
            });
            jobId = new URL(statusUrl).pathname.split('/').pop();

            cluMigrationJobsStoreHouse.setItems([
                new CluMigrationJobStore(jobId, {
                    status: 'inProgress',
                    luisAppsMigrationState: luisAppsMetadata.map(app => ({
                        appId: app.appId,
                        appName: null,
                        status: 'notStarted',
                        failureReason: null
                    })),
                    errors: [],
                    warnings: []
                })
            ]);

            cluMigrationJobsStoreHouse.tracker.setState('completed');

            await migrationService.pollMigration({
                statusUrl,
                jobId,
                languageResourceKey,
                languageResourceEndpoint: languageResource.endpoint,
                operationHandler: getMigrationJobOperationHandler(lifetime, cluMigrationJobsStoreHouse)
            });
        } catch (e) {
            if (jobId) {
                cluMigrationJobsStoreHouse.findById(jobId).setStatus('failed');
            }
            cluMigrationJobsStoreHouse.tracker.setState('failed', e);

            throw e;
        }
    };

    const intializeMigration = async ({ applications }: HandlerParams<{ applications: LuisAppStore[] }>) => {
        rootStore.cluMigrationStoreHouse.setSelectedApps([...applications]);
        uiStore.setIsCluMigrationModalHidden(false);
    };

    const dismissCluMigration = async () => {
        uiStore.setIsCluMigrationModalHidden(true);
        rootStore.cluMigrationStoreHouse.setSelectedApps([]);
    };

    return { migrateLuisApps, intializeMigration, dismissCluMigration };
};
